import type { ContentType } from '@/interfaces';
import clientStorage from '@/services/ClientStorage';
import { OFFLINE_STORAGE_NAMESPACE } from './constants';
import {
    clearAllItemsFromDb,
    deleteExpiredItemsFromDb,
    deleteItemFromDb,
    evictExpiredBlobsForUserFromDb,
    getAllItemsForUser,
    getItemFromDb,
    purgeItemsNotForUserFromDb,
    saveItemToDb,
} from './db';
import { supportsServiceWorker } from './serviceWorker';
import type { OfflineDownloadArgs, OfflineItem, OfflineItemMeta } from './types';

export type { OfflineItem, OfflineItemMeta } from './types';

/** True when the browser supports the storage APIs the offline reader needs. */
export function canUseOfflineReports(): boolean {
    return typeof window !== 'undefined' && 'indexedDB' in window;
}

/** True when offline reading can work end to end: storage for the PDFs and a service worker to serve the reader offline. */
export function canUseOfflineMode(): boolean {
    return canUseOfflineReports() && supportsServiceWorker();
}

/**
 * Fetches a PDF while online and stores it (bytes + metadata)
 * in IndexedDB under the given user.
 */
export async function downloadItemForOfflineReading(
    userId: number,
    { contentType, itemId, title, viewerUrl, pdfUrl, watermarkText }: OfflineDownloadArgs
): Promise<void> {
    if (!canUseOfflineReports()) {
        throw new Error('Offline reading is not supported in this browser.');
    }
    if (await getItemFromDb(userId, contentType, itemId)) return;

    const response = await fetch(pdfUrl);
    if (!response.ok) {
        throw new Error(`Failed to download PDF (${response.status}).`);
    }
    const pdf = await response.blob();

    await saveItemToDb(userId, {
        itemId,
        contentType,
        title,
        viewerUrl,
        downloadedAt: new Date().toISOString(),
        pdf,
        watermarkText,
    });
}

/** Returns the given user's downloaded items as metadata (without the heavy Blob loaded into memory). */
export async function listOfflineItems(userId: number): Promise<OfflineItemMeta[]> {
    const items = await getAllItemsForUser(userId);
    return items.map(({ pdf: _pdf, ...meta }: OfflineItem) => meta);
}

/**
 * Returns a short-lived blob: URL for a downloaded PDF, or null if not found.
 * The caller MUST call URL.revokeObjectURL() when done.
 */
export async function getOfflineItemPdfUrl(
    userId: number,
    contentType: ContentType,
    itemId: string
): Promise<string | null> {
    const item = await getItemFromDb(userId, contentType, itemId);
    if (!item?.pdf) return null;
    return URL.createObjectURL(item.pdf);
}

export async function deleteOfflineItem(userId: number, contentType: ContentType, itemId: string): Promise<void> {
    await deleteItemFromDb(userId, contentType, itemId);
}

/** Removes every locally stored offline related data. */
export async function clearAllOfflineData(): Promise<void> {
    clientStorage.clearNamespace(OFFLINE_STORAGE_NAMESPACE);
    await clearAllItemsFromDb();
}

export async function purgeItemsNotForUser(userId: number): Promise<number> {
    if (!canUseOfflineReports()) return 0;
    return purgeItemsNotForUserFromDb(userId);
}

export async function evictExpiredContentBlobs(userId: number): Promise<number> {
    if (!canUseOfflineReports()) return 0;
    return evictExpiredBlobsForUserFromDb(userId);
}

export async function deleteExpiredOfflineItems(): Promise<number> {
    if (!canUseOfflineReports()) return 0;
    return deleteExpiredItemsFromDb();
}
